import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  Table,
  TableColumnsType,
  Tabs,
  Tag,
  Upload,
} from 'antd';
import * as Component from 'components';
import CustomInputNumber from 'components/CustomInputNumber';
import { EnhancedCastEditor } from 'components/enhanced-cast';
import CustomSelector, { CustomSelectorTabInterface } from 'components/selector/Selector';
import {
  TransferDataHandlerInterface,
  TransferDataSourceInterface,
} from 'components/transfer/Transfer';
import { AGE_RATING } from 'constants/constant';
import { THUMBNAIL_UPLOAD_STATE } from 'constants/index';
import { episodeService } from 'content/episode/services/episodeService';
import { EpisodeInterface } from 'content/episode/types/episodeType';
import { globalSettingService } from 'content/global-settings/services/globalSettingService';
import {
  CountryInterface,
  LanguageInterface,
} from 'content/global-settings/types/globalSettingType';
import { seasonService } from 'content/service';
import { seriesSeasonItemService } from 'content/service/seriesSeasonService';
import { seriesService } from 'content/service/seriesService';
import { PosterInterface, ThumbnailInterface } from 'content/type';
import { SeriesInterface } from 'content/type/seriesType';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { genreApiService } from 'service/api/genreApiService';
import { peopleApiService } from 'service/api/peopleApiService';
import { tagApiService } from 'service/api/tagApiService';
import { GenreInterface } from 'type/genreType';
import { PeopleInterface } from 'type/peopleType';
import { ElevenDbSeasonInterface, SeasonData } from 'type/seasonType';
import { TagInterface } from 'type/tagType';
import { slugify } from 'utils/slugify';
import {
  createDefaultDistributions,
  ENVIRONMENTS,
  getApiServiceName,
  SERVICE_OPTIONS,
  SUBTITLE_LANGUAGES,
} from '../../../constants/serviceDistribution';
import odkAdminApiService from '../../../shared/service/odxAdminApiService';
import { I18nFieldData } from '../../../shared/types/i18n/i18nTypes';
import posterHandlers from '../season/handlers/PosterHandlers';
import { seasonContentService } from '../season/services/seasonContentService';
import { SeasonContentInterface } from '../season/types/seasonContentTypes';
import './SeasonEdit2.css';

const SeasonEdit2: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const seriesIdParam = searchParams.get('series_id');
  const [season, setSeason] = useState<SeasonData | null>(null);
  const [series, setSeries] = useState<SeriesInterface | null>(null);
  const [countries, setCountries] = useState<CountryInterface[]>([]);
  const [writeLanguages, setWriteLanguages] = useState<LanguageInterface[]>([]);
  const [searchedPeopleList, setSearchedPeopleList] = useState<PeopleInterface[]>([]);
  const [genreList, setGenreList] = useState<GenreInterface[]>([]);
  const [tagList, setTagList] = useState<TagInterface[]>([]);
  const [enhancedCast, setEnhancedCast] = useState<{ cast: number; type: string }[]>([]);

  // i18n 필드 상태
  const [i18nTitle, setI18nTitle] = useState<I18nFieldData>({});
  const [i18nSynopsis, setI18nSynopsis] = useState<I18nFieldData>({});
  const [i18nSeoTitle, setI18nSeoTitle] = useState<I18nFieldData>({});
  const [i18nSeoSynopsis, setI18nSeoSynopsis] = useState<I18nFieldData>({});
  const [i18nSeoKeyword, setI18nSeoKeyword] = useState<I18nFieldData>({});

  // Thumbnail/Poster states
  const [newThumbnailList, setNewThumbnailList] = useState<ThumbnailInterface[]>([]);
  const [uploadedThumbnailList, setUploadedThumbnailList] = useState<ThumbnailInterface[]>([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState<ThumbnailInterface | null>(null);
  const [removedUids, setRemovedUids] = useState<string[]>([]);

  // 11DB Metadata states
  const [elevenDbSeasonId, setElevenDbSeasonId] = useState<string>('');
  const [elevenDbAwards, setElevenDbAwards] = useState<string>('');
  const [elevenDbAwardsCategory, setElevenDbAwardsCategory] = useState<string>('');
  const [elevenDbRelatedSeries, setElevenDbRelatedSeries] = useState<string>('');
  const [elevenDbRelatedTitles, setElevenDbRelatedTitles] = useState<string>('');
  const [elevenDbLinkedArticles, setElevenDbLinkedArticles] = useState<string>('');
  const [elevenDbUserRatings, setElevenDbUserRatings] = useState<string>('');
  const [elevenDbCriticsRatings, setElevenDbCriticsRatings] = useState<string>('');
  const [elevenDbMovieBoxOffice, setElevenDbMovieBoxOffice] = useState<string>('');

  // 신규 생성 시 선택한 에피소드 ID들을 저장 (저장 시 함께 처리)
  const [pendingEpisodesToLink, setPendingEpisodesToLink] = useState<number[]>([]);
  const [pendingEpisodesToUnlink, setPendingEpisodesToUnlink] = useState<number[]>([]);
  // 신규 생성 시 선택한 에피소드 데이터를 캐시 (Transfer 오른쪽 표시용)
  const [pendingEpisodesData, setPendingEpisodesData] = useState<
    Map<number, TransferDataSourceInterface>
  >(new Map());

  // Service Auto Distribution
  const [inherit, setInherit] = useState(true); // true: 상속받기, false: 자체 설정
  const [distributions, setDistributions] = useState<any[]>([]);

  // 변경된 필드 추적용
  const changedFieldsRef = useRef<Set<string>>(new Set());

  const markFieldChanged = (...fields: string[]) => {
    fields.forEach(field => {
      changedFieldsRef.current.add(field);
    });
  };

  const handleValuesChange = (changedValues: any) => {
    Object.keys(changedValues).forEach(key => {
      markFieldChanged(key);
    });
  };

  // CP from ODK, ODC states
  const [cpFromOdkOptions, setCpFromOdkOptions] = useState<any[]>([]);
  const [cpFromOdcOptions, setCpFromOdcOptions] = useState<any[]>([]);
  const [defaultCpFromOdcOptions, setDefaultCpFromOdcOptions] = useState<any[]>([]);
  const [cpFromOdcLoading, setCpFromOdcLoading] = useState(false);

  const POSTER_LANGUAGE_OPTIONS = [
    { value: 'EN', label: 'English', flag: '🇺🇸' },
    { value: 'KO', label: 'Korean', flag: '🇰🇷' },
    { value: 'ZH-HANS', label: 'Simplified Chinese', flag: '🇨🇳' },
    { value: 'VI', label: 'Vietnamese', flag: '🇻🇳' },
  ];

  const WEEKDAYS = [
    { label: 'Monday', value: 'monday', short: 'Mon' },
    { label: 'Tuesday', value: 'tuesday', short: 'Tue' },
    { label: 'Wednesday', value: 'wednesday', short: 'Wed' },
    { label: 'Thursday', value: 'thursday', short: 'Thu' },
    { label: 'Friday', value: 'friday', short: 'Fri' },
    { label: 'Saturday', value: 'saturday', short: 'Sat' },
    { label: 'Sunday', value: 'sunday', short: 'Sun' },
  ];

  const initializeDefaultDistributions = () => {
    setDistributions(createDefaultDistributions());
  };

  const updateDistribution = (serviceName: string, environment: string, updates: any) => {
    setDistributions(prev =>
      prev.map(dist =>
        dist.service_name === serviceName && dist.environment === environment
          ? { ...dist, ...updates }
          : dist
      )
    );
  };

  // Poster handlers (시즌 포스터 업로드용)
  // 신규 생성 시에는 seasonId가 없으므로, 나중에 동적으로 생성
  const getPosterHandlers = (seasonIdForPoster: number) => {
    return posterHandlers({
      form,
      seasonId: seasonIdForPoster,
      selectedPoster: selectedThumbnail as any,
      setSelectedPoster: setSelectedThumbnail as any,
      setNewPosterList: setNewThumbnailList as any,
      setUploadedPosterList: setUploadedThumbnailList as any,
      removedUids,
      setRemovedUids,
    });
  };

  // 기존 시즌 편집 시에는 초기화 시점에 handlers 생성
  const existingPosterHandlers = id && id !== 'new' ? getPosterHandlers(Number(id)) : null;
  const { handlePosterUpload } = existingPosterHandlers || {
    handlePosterUpload: async () => {},
  };

  // 포스터 제거 핸들러 (시리즈와 동일한 로직)
  const handlePosterRemove = (poster: ThumbnailInterface) => {
    if (poster.id && poster.id > 0) {
      // 업로드된 포스터 제거
      // posterList는 원본 데이터로 유지하고, uploadedPosterList에서만 제거
      // 저장 시 posterList와 uploadedPosterList를 비교해서 삭제할 포스터를 찾음
      const currentUploadedList = form.getFieldValue('uploadedPosterList') || [];
      const currentNewList = form.getFieldValue('newPosterList') || [];

      // uploadedPosterList에서 제거
      const updatedUploadedList = currentUploadedList.filter(
        (p: ThumbnailInterface) => p.id !== poster.id
      );

      // newThumbnailList에서도 제거 (혹시 있을 경우)
      const updatedNewList = currentNewList.filter(
        (p: ThumbnailInterface) => !(p.id && p.id === poster.id)
      );

      // 폼 필드와 상태 동기화
      form.setFieldValue('uploadedPosterList', updatedUploadedList);
      form.setFieldValue('newPosterList', updatedNewList);
      setUploadedThumbnailList(updatedUploadedList);
      setNewThumbnailList(updatedNewList);

      // 선택된 포스터가 삭제된 경우 선택 해제
      if (selectedThumbnail && selectedThumbnail.id === poster.id) {
        setSelectedThumbnail(null);
        form.setFieldValue('selectedPoster', null);
      }
    } else {
      // 새로 추가한 포스터 제거
      const currentNewList = form.getFieldValue('newPosterList') || [];
      const currentNewThumbnailList = form.getFieldValue('newThumbnailList') || [];

      // uid로 필터링
      const updatedNewList = currentNewList.filter((p: ThumbnailInterface) => p.uid !== poster.uid);
      const updatedNewThumbnailList = currentNewThumbnailList.filter(
        (p: ThumbnailInterface) => p.uid !== poster.uid
      );

      // 폼 필드와 상태 동기화
      form.setFieldValue('newPosterList', updatedNewList);
      form.setFieldValue('newThumbnailList', updatedNewThumbnailList);
      setNewThumbnailList(updatedNewThumbnailList);

      // removedUids에 추가 (같은 uid로 다시 업로드되는 것을 방지)
      if (poster.uid) {
        setRemovedUids(prev => {
          if (!prev.includes(poster.uid!)) {
            return [...prev, poster.uid!];
          }
          return prev;
        });
      }
    }
  };

  // Language normalization
  const normalizeLanguage = (lang?: string): string => {
    if (!lang) return 'EN';
    const upperLang = lang.toUpperCase();
    if (upperLang === 'ZH' || upperLang === 'ZH-CN') return 'ZH-HANS';
    return upperLang;
  };

  // Upload handler with language and orientation
  const handleUploadWithLanguageAndOrientation =
    (language: string, orientation: string) => (info: any) => {
      const { fileList } = info;

      // 폼에서 현재 상태 가져오기
      const existingNewList = form.getFieldValue('newPosterList') || [];
      const existingNewThumbnailList = form.getFieldValue('newThumbnailList') || [];
      const existingUploadedList = form.getFieldValue('uploadedPosterList') || [];

      // 기존 리스트의 uid와 removedUids 체크
      const existingUids = new Set([
        ...existingNewList.map((p: ThumbnailInterface) => p.uid).filter(Boolean),
        ...existingNewThumbnailList.map((p: ThumbnailInterface) => p.uid).filter(Boolean),
        ...existingUploadedList.map((p: ThumbnailInterface) => p.uid).filter(Boolean),
      ]);

      // removedUids에 포함되지 않고, 기존에 없는 uid만 필터링
      const filteredFileList = fileList.filter(
        (file: any) => !removedUids.includes(file.uid) && !existingUids.has(file.uid)
      );

      // 새로 추가된 파일만 변환
      const uploadThumbnailList = filteredFileList.map((file: any, index: number) => {
        const fileUrl = file.originFileObj ? URL.createObjectURL(file.originFileObj) : '';
        const thumbnail = {
          id: -(existingNewList.length + existingNewThumbnailList.length + index + 1),
          uid: file.uid,
          file: fileUrl,
          is_representative: false,
          state: THUMBNAIL_UPLOAD_STATE.UPLOADING,
          originFileObj: file.originFileObj,
          orientation: orientation,
          language: normalizeLanguage(language),
        } as ThumbnailInterface;
        return thumbnail;
      });

      // 기존 리스트와 합치기
      const combinedNewList = [...existingNewList, ...uploadThumbnailList];
      const combinedNewThumbnailList = [...existingNewThumbnailList, ...uploadThumbnailList];

      // 폼 필드와 상태 동기화
      form.setFieldValue('newPosterList', combinedNewList);
      form.setFieldValue('newThumbnailList', combinedNewThumbnailList);
      setNewThumbnailList(combinedNewThumbnailList);

      // removedUids에서 제거 (다시 업로드했으므로)
      if (uploadThumbnailList.length > 0) {
        const newUids = uploadThumbnailList.map((p: ThumbnailInterface) => p.uid).filter(Boolean);
        setRemovedUids(prev => prev.filter(uid => !newUids.includes(uid)));
      }
    };

  const fetchElevenDbSeason = async () => {
    const response = await seasonService.fetchElevenDbSeason(Number(id));
    setElevenDbSeasonId(response.eleven_db_id || '');
    setElevenDbAwards(response.awards || '');
    setElevenDbAwardsCategory(response.awards_category || '');
    setElevenDbRelatedSeries(response.related_series || '');
    setElevenDbRelatedTitles(response.related_titles || '');
    setElevenDbLinkedArticles(response.linked_articles || '');
    setElevenDbUserRatings(response.user_ratings || '');
    setElevenDbCriticsRatings(response.critics_ratings || '');
    setElevenDbMovieBoxOffice(response.movie_box_office || '');
  };

  const fetchCpFromOdk = async () => {
    const response = await odkAdminApiService.fetchCpFromOdk();
    setCpFromOdkOptions(response);
  };

  const fetchCpFromOdc = async () => {
    const response = await odkAdminApiService.fetchCpFromOdc();
    setCpFromOdcOptions(response);
    setDefaultCpFromOdcOptions(response);
  };

  // ODC 검색 함수 (debounce 적용)
  const debouncedSearchCpFromOdc = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setCpFromOdcOptions(defaultCpFromOdcOptions);
        return;
      }

      setCpFromOdcLoading(true);
      try {
        const result = await odkAdminApiService.searchCpFromOdc(searchTerm);
        setCpFromOdcOptions(result);
      } catch (error) {
        console.error('ODC CP search failed:', error);
        setCpFromOdcOptions([]);
      } finally {
        setCpFromOdcLoading(false);
      }
    }, 300),
    [defaultCpFromOdcOptions]
  );

  // ODC Select 박스가 열릴 때 기본 목록 복원
  const handleCpFromOdcDropdownVisibleChange = (open: boolean) => {
    if (open) {
      setCpFromOdcOptions(defaultCpFromOdcOptions);
    }
  };

  // ODC 검색어 변경 처리
  const handleCpFromOdcSearch = (value: string) => {
    if (!value.trim()) {
      setCpFromOdcOptions(defaultCpFromOdcOptions);
    } else {
      debouncedSearchCpFromOdc(value);
    }
  };

  // ODC X 버튼으로 검색어 지웠을 때 처리
  const handleCpFromOdcClear = () => {
    setCpFromOdcOptions(defaultCpFromOdcOptions);
  };

  useEffect(() => {
    // id가 있고 'new'가 아니면 기존 season 편집
    if (id && id !== 'new') {
      fetchSeason();
      fetchElevenDbSeason();
    } else {
      // id가 없거나 'new'이면 신규 생성
      initializeDefaultDistributions();
      // series_id가 있을 때만 시리즈 정보를 가져옴
      if (seriesIdParam) {
        if (seriesIdParam === 'pending') {
          // 시리즈가 아직 저장되지 않은 경우, sessionStorage에서 데이터 가져오기
          loadPendingSeriesData();
        } else {
          fetchSeriesInfo(Number(seriesIdParam), id || 'new');
        }
      }
      // series_id가 없어도 신규 생성은 가능하므로 에러 처리하지 않음
    }
    fetchCountries();
    fetchWriteLanguages();
    fetchGenres();
    fetchTags();
    fetchCpFromOdk();
    fetchCpFromOdc();
  }, [id, seriesIdParam]);

  const loadPendingSeriesData = async () => {
    try {
      const pendingSeriesDataStr = sessionStorage.getItem('pendingSeriesData');
      if (!pendingSeriesDataStr) {
        console.warn('No pending series data found in sessionStorage');
        return;
      }

      const pendingSeriesData = JSON.parse(pendingSeriesDataStr);

      // 시리즈의 마지막 season number를 찾아서 +1 설정
      // 시리즈가 아직 저장되지 않았으므로 기본값 1로 설정
      const nextSeasonNumber = 1;
      form.setFieldValue('season_order', nextSeasonNumber);

      // 시리즈 slug와 season number를 기반으로 slug 자동 생성
      const seriesSlug = pendingSeriesData.slug || '';
      if (seriesSlug) {
        const seasonSlug = `${seriesSlug}-season-${nextSeasonNumber}`;
        form.setFieldValue('slug', seasonSlug);
      } else if (pendingSeriesData.i18n_title?.en) {
        // 시리즈 slug가 없으면 title_en을 기반으로 slug 생성
        const autoSlug = slugify(pendingSeriesData.i18n_title.en);
        if (autoSlug) {
          const seasonSlug = `${autoSlug}-season-${nextSeasonNumber}`;
          form.setFieldValue('slug', seasonSlug);
        }
      }

      // 시리즈 타이틀과 season number를 기반으로 타이틀 자동 생성
      const seasonTitle: I18nFieldData = {};
      if (pendingSeriesData.i18n_title?.en) {
        seasonTitle.en = `${pendingSeriesData.i18n_title.en}`;
      }
      if (pendingSeriesData.i18n_title?.ko) {
        seasonTitle.ko = `${pendingSeriesData.i18n_title.ko}`;
      }
      if (pendingSeriesData.i18n_title?.['zh-hans']) {
        seasonTitle['zh-hans'] = `${pendingSeriesData.i18n_title['zh-hans']}`;
      }
      if ((pendingSeriesData.i18n_title as any)?.vi) {
        (seasonTitle as any).vi = `${(pendingSeriesData.i18n_title as any).vi}`;
      }
      setI18nTitle(seasonTitle);
      form.setFieldsValue({
        season_title_en: seasonTitle.en || '',
        season_title_ko: seasonTitle.ko || '',
        season_title_zh_hans: seasonTitle['zh-hans'] || '',
        season_title_vi: (seasonTitle as any).vi || '',
      });

      // 시리즈의 다른 메타데이터 복사
      if (pendingSeriesData.i18n_synopsis) {
        setI18nSynopsis(pendingSeriesData.i18n_synopsis);
      }
      if (pendingSeriesData.i18n_seo_title) {
        setI18nSeoTitle(pendingSeriesData.i18n_seo_title);
      }
      if (pendingSeriesData.i18n_seo_synopsis) {
        setI18nSeoSynopsis(pendingSeriesData.i18n_seo_synopsis);
      }
      if (pendingSeriesData.i18n_seo_keyword) {
        setI18nSeoKeyword(pendingSeriesData.i18n_seo_keyword);
      }

      // 기타 메타데이터 복사
      if (pendingSeriesData.age_rating) {
        form.setFieldValue('age_rating', pendingSeriesData.age_rating);
      }
      if (pendingSeriesData.country_of_origin) {
        form.setFieldValue('country_of_origin', pendingSeriesData.country_of_origin);
      } else if (pendingSeriesData.produce_country) {
        form.setFieldValue('country_of_origin', pendingSeriesData.produce_country);
      }
      if (pendingSeriesData.dubbing_mode) {
        form.setFieldValue('dubbing_mode', pendingSeriesData.dubbing_mode);
      }
      if (pendingSeriesData.production_year) {
        form.setFieldValue('production_year', pendingSeriesData.production_year);
      } else if (pendingSeriesData.produce_year) {
        form.setFieldValue('production_year', pendingSeriesData.produce_year);
      }
      if (pendingSeriesData.release_date_start) {
        form.setFieldValue('release_date_start', dayjs(pendingSeriesData.release_date_start));
      }
      if (pendingSeriesData.release_date_end) {
        form.setFieldValue('release_date_end', dayjs(pendingSeriesData.release_date_end));
      }
      if (pendingSeriesData.release_weekday) {
        form.setFieldValue('release_weekday', pendingSeriesData.release_weekday);
      }
      if (pendingSeriesData.tags) {
        form.setFieldValue('tags', pendingSeriesData.tags);
      }
      if (pendingSeriesData.genres) {
        form.setFieldValue('genres', pendingSeriesData.genres);
      }
      if (pendingSeriesData.cp_from_odk) {
        form.setFieldValue('cp_from_odk', pendingSeriesData.cp_from_odk);
      }
      if (pendingSeriesData.cp_from_odc) {
        form.setFieldValue('cp_from_odc', pendingSeriesData.cp_from_odc);
      }
      if (pendingSeriesData.enhanced_cast) {
        setEnhancedCast(
          pendingSeriesData.enhanced_cast.map((item: any) => ({
            cast: item.cast,
            type: item.type,
          }))
        );
      }

      // Service Auto Distribution 설정 복사
      const inheritValue = pendingSeriesData.service_auto_distribution_inherit;
      setInherit(inheritValue !== false); // undefined나 true면 상속, false면 자체 설정

      if (
        pendingSeriesData.service_auto_distributions &&
        pendingSeriesData.service_auto_distributions.length > 0
      ) {
        const apiDistributions = pendingSeriesData.service_auto_distributions;
        const formattedDistributions: any[] = [];

        SERVICE_OPTIONS.forEach(service => {
          ENVIRONMENTS.forEach(env => {
            const apiServiceName = getApiServiceName(service.name, env);
            const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

            if (apiDist) {
              formattedDistributions.push({
                id: apiDist.id,
                service_name: service.name,
                environment: env,
                enabled: true,
                auto_publish: apiDist.auto_publish,
                subtitle_langs:
                  apiDist.subtitle_langs ||
                  (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
              });
            } else {
              formattedDistributions.push({
                service_name: service.name,
                environment: env,
                enabled: false,
                auto_publish: false,
                subtitle_langs: [],
              });
            }
          });
        });

        setDistributions(formattedDistributions);
      } else {
        initializeDefaultDistributions();
      }
    } catch (error) {
      console.error('Failed to load pending series data:', error);
    }
  };

  const fetchSeriesInfo = async (seriesId: number, seasonId?: string) => {
    try {
      const seriesData = await seriesService.fetchById(seriesId);
      setSeries(seriesData);

      // 새 Season인 경우 시리즈의 마지막 season number를 찾아서 +1 설정
      // series_id가 있을 때만 실행
      if (seasonId === 'new' && seriesId) {
        try {
          const queryParams = new URLSearchParams();
          queryParams.append('order_by', 'season_order');
          queryParams.append('order_direction', 'desc');
          queryParams.append('page_size', '100'); // 충분한 수를 가져와서 모든 season number 확인
          const seasons = await seriesSeasonItemService.fetchChildItemsByParentId(
            seriesId,
            queryParams
          );

          // season_number 또는 season_order 중 가장 큰 값 찾기
          const seasonNumbers = seasons
            .map(s => (s as any).season_order)
            .filter(
              (num): num is number => num !== null && num !== undefined && !isNaN(Number(num))
            );

          const maxSeasonNumber = seasonNumbers.length > 0 ? Math.max(...seasonNumbers) : 0;
          const nextSeasonNumber = maxSeasonNumber + 1;
          form.setFieldValue('season_order', nextSeasonNumber);

          // 시리즈 slug와 season number를 기반으로 slug 자동 생성
          const seriesSlug = seriesData.slug || '';
          if (seriesSlug) {
            const seasonSlug = `${seriesSlug}-season-${nextSeasonNumber}`;
            form.setFieldValue('slug', seasonSlug);
          }

          // 시리즈 타이틀과 season number를 기반으로 타이틀 자동 생성
          const seasonTitle: I18nFieldData = {};
          if (seriesData.i18n_title) {
            if (seriesData.i18n_title.en) {
              seasonTitle.en = `${seriesData.i18n_title.en}`;
            }
            if (seriesData.i18n_title.ko) {
              seasonTitle.ko = `${seriesData.i18n_title.ko}`;
            }
            if (seriesData.i18n_title['zh-hans']) {
              seasonTitle['zh-hans'] = `${seriesData.i18n_title['zh-hans']}`;
            }
            if ((seriesData.i18n_title as any).vi) {
              (seasonTitle as any).vi = `${(seriesData.i18n_title as any).vi}`;
            }
            setI18nTitle(seasonTitle);
            form.setFieldsValue({
              season_title_en: seasonTitle.en || '',
              season_title_ko: seasonTitle.ko || '',
              season_title_zh_hans: seasonTitle['zh-hans'] || '',
              season_title_vi: (seasonTitle as any).vi || '',
            });
          }
          if (seriesData.i18n_synopsis) {
            setI18nSynopsis(seriesData.i18n_synopsis);
          }
          if (seriesData.i18n_seo_title) {
            setI18nSeoTitle(seriesData.i18n_seo_title);
          }
          if (seriesData.i18n_seo_synopsis) {
            setI18nSeoSynopsis(seriesData.i18n_seo_synopsis);
          }
          if (seriesData.i18n_seo_keyword) {
            setI18nSeoKeyword(seriesData.i18n_seo_keyword);
          }
          // 기타 메타데이터 복사
          if (seriesData.age_rating) {
            form.setFieldValue('age_rating', seriesData.age_rating);
          }
          // country_of_origin 또는 produce_country 중 하나라도 있으면 복사
          if ((seriesData as any).country_of_origin) {
            form.setFieldValue('country_of_origin', (seriesData as any).country_of_origin);
          } else if (seriesData.produce_country) {
            form.setFieldValue('country_of_origin', seriesData.produce_country);
          }
          if (seriesData.dubbing_mode) {
            form.setFieldValue('dubbing_mode', seriesData.dubbing_mode);
          }
          // production_year 또는 produce_year 중 하나라도 있으면 복사
          if ((seriesData as any).production_year) {
            form.setFieldValue('production_year', (seriesData as any).production_year);
          } else if ((seriesData as any).produce_year) {
            form.setFieldValue('production_year', (seriesData as any).produce_year);
          }
          // 날짜 필드 복사
          if (seriesData.release_date_start) {
            form.setFieldValue('release_date_start', dayjs(seriesData.release_date_start));
          }
          if (seriesData.release_date_end) {
            form.setFieldValue('release_date_end', dayjs(seriesData.release_date_end));
          }
          // Release weekday 복사
          if ((seriesData as any).release_weekday) {
            form.setFieldValue('release_weekday', (seriesData as any).release_weekday);
          }
          if ((seriesData as any).tags) {
            form.setFieldValue('tags', (seriesData as any).tags);
          }
          if (seriesData.enhanced_cast) {
            setEnhancedCast(
              seriesData.enhanced_cast.map(item => ({
                cast: item.cast,
                type: item.type,
              }))
            );
            // Enhanced Cast에 있는 cast ID들에 대한 people 정보 가져오기
            const castIds = seriesData.enhanced_cast.map(item => item.cast);
            if (castIds.length > 0) {
              try {
                const peopleResult = await peopleApiService.fetchByIds(castIds);
                if (peopleResult.results && peopleResult.results.length > 0) {
                  setSearchedPeopleList(prev => {
                    const existingIds = new Set(prev.map(p => p.id));
                    const newPeople = peopleResult.results.filter(p => !existingIds.has(p.id));
                    return [...prev, ...newPeople];
                  });
                }
              } catch (error) {
                console.error('Failed to fetch people for enhanced cast:', error);
              }
            }
          }
        } catch (error) {
          console.error('Failed to fetch series seasons:', error);
          // 에러 발생 시 기본값 1로 설정
          form.setFieldValue('season_order', 1);
          // 기본 slug 생성
          const seriesSlug = seriesData.slug || '';
          if (seriesSlug) {
            form.setFieldValue('slug', `${seriesSlug}-season-1`);
          }
          // 기본 날짜 필드 복사
          if (seriesData.release_date_start) {
            form.setFieldValue('release_date_start', dayjs(seriesData.release_date_start));
          }
          if (seriesData.release_date_end) {
            form.setFieldValue('release_date_end', dayjs(seriesData.release_date_end));
          }
          if ((seriesData as any).release_weekday) {
            form.setFieldValue('release_weekday', (seriesData as any).release_weekday);
          }
          // 기본 타이틀 생성
          if (seriesData.i18n_title) {
            const seasonTitle: I18nFieldData = {};
            if (seriesData.i18n_title.en) {
              seasonTitle.en = `${seriesData.i18n_title.en}`;
            }
            if (seriesData.i18n_title.ko) {
              seasonTitle.ko = `${seriesData.i18n_title.ko}`;
            }
            if (seriesData.i18n_title['zh-hans']) {
              seasonTitle['zh-hans'] = `${seriesData.i18n_title['zh-hans']}`;
            }
            if ((seriesData.i18n_title as any).vi) {
              (seasonTitle as any).vi = `${(seriesData.i18n_title as any).vi}`;
            }
            setI18nTitle(seasonTitle);
            form.setFieldsValue({
              season_title_en: seasonTitle.en || '',
              season_title_ko: seasonTitle.ko || '',
              season_title_zh_hans: seasonTitle['zh-hans'] || '',
              season_title_vi: (seasonTitle as any).vi || '',
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch series info:', error);
    }
  };

  const fetchSeason = async () => {
    try {
      const data = await seasonService.fetchSeasonWithI18n(Number(id));
      setSeason(data);

      // series_id 또는 series 필드 중 하나라도 있으면 시리즈 정보 가져오기
      const seriesId = data.series_id || (data as any).series;
      if (seriesId) {
        await fetchSeriesInfo(seriesId, id);
      }

      // Form 값 설정
      const formattedData: any = {
        ...data,
        // 필드명 매핑
        season_order: (data as any).season_order || null,
        country_of_origin: (data as any).country_of_origin || null,
        genres: (data as any).genres || [],
        series_id: (data as any).series_id || (data as any).series || null,
        // 날짜 필드 변환
        release_date_start: data.release_date_start ? dayjs(data.release_date_start) : null,
        release_date_end: data.release_date_end ? dayjs(data.release_date_end) : null,
        // Release weekday
        release_weekday: (data as any).release_weekday || '',
        // 상태 필드
        status: data.status === 'published' ? 'published' : 'unpublished',
        // 레거시 필드 매핑 (form 필드명에 맞춤)
        season_title_en: data.i18n_title?.en || data.title_en || data.title || '',
        season_title_ko: data.i18n_title?.ko || '',
        season_title_zh_hans: data.i18n_title?.['zh-hans'] || '',
        season_title_vi: (data.i18n_title as any)?.vi || '',
        // total_episodes는 API에서 null이 올 수 있으므로 제외하고 나중에 설정
      };
      form.setFieldsValue(formattedData);

      // Season contents 정보로 Total # of Episodes 자동 설정 (formattedData 설정 후)
      try {
        const seasonContents = await seasonContentService.getSeasonContents(Number(id));
        // episode 타입만 카운트 (clip/trailer 제외)
        const episodeCount = seasonContents.contents.length;
        console.log('episodeCount', episodeCount);
        // API에서 total_episodes가 null이거나 0이면 contents 개수로 설정
        if (
          episodeCount > 0 &&
          (!(data as any).total_episodes || (data as any).total_episodes === 0)
        ) {
          form.setFieldValue('total_episodes', episodeCount);
        } else if ((data as any).total_episodes) {
          // API에 값이 있으면 그대로 사용
          form.setFieldValue('total_episodes', (data as any).total_episodes);
        }
      } catch (error) {
        console.error('Failed to fetch season contents for episode count:', error);
        // 에러가 발생해도 계속 진행
      }

      // i18n 필드 초기화
      if (data.i18n_title) setI18nTitle(data.i18n_title);
      if (data.i18n_synopsis) setI18nSynopsis(data.i18n_synopsis);
      if (data.i18n_seo_title) setI18nSeoTitle(data.i18n_seo_title);
      if (data.i18n_seo_synopsis) setI18nSeoSynopsis(data.i18n_seo_synopsis);
      if (data.i18n_seo_keyword) setI18nSeoKeyword(data.i18n_seo_keyword);

      // Genres와 Tags 가져오기
      const genreIds = (data as any).genres || [];
      if (genreIds.length > 0) {
        try {
          const genreResponse = await genreApiService.fetchByIds(genreIds);
          setGenreList(genreResponse.results);
        } catch (error) {
          console.error('Failed to fetch genres:', error);
        }
      }
      const tagIds = (data as any).tags || [];
      if (tagIds.length > 0) {
        try {
          // tags는 ID 배열이거나 Tag 객체 배열일 수 있음
          const tagIdArray = tagIds.map((t: any) => (typeof t === 'number' ? t : t.id));
          const tagResponse = await tagApiService.fetchByIds(tagIdArray);
          setTagList(tagResponse.results);
        } catch (error) {
          console.error('Failed to fetch tags:', error);
        }
      }

      // Enhanced Cast 초기화
      if (data.enhanced_cast) {
        setEnhancedCast(
          data.enhanced_cast.map(item => ({
            cast: item.cast,
            type: item.type,
          }))
        );
        // Enhanced Cast에 있는 cast ID들에 대한 people 정보 가져오기
        const castIds = data.enhanced_cast.map(item => item.cast);
        if (castIds.length > 0) {
          try {
            const peopleResult = await peopleApiService.fetchByIds(castIds);
            if (peopleResult.results && peopleResult.results.length > 0) {
              setSearchedPeopleList(prev => {
                const existingIds = new Set(prev.map(p => p.id));
                const newPeople = peopleResult.results.filter(p => !existingIds.has(p.id));
                return [...prev, ...newPeople];
              });
            }
          } catch (error) {
            console.error('Failed to fetch people for enhanced cast:', error);
          }
        }
      }

      // Service Auto Distribution 설정
      const inheritValue = (data as any).service_auto_distribution_inherit;
      setInherit(inheritValue !== false); // undefined나 true면 상속, false면 자체 설정

      if (
        (data as any).service_auto_distributions &&
        (data as any).service_auto_distributions.length > 0
      ) {
        const apiDistributions = (data as any).service_auto_distributions;
        const formattedDistributions: any[] = [];

        SERVICE_OPTIONS.forEach(service => {
          ENVIRONMENTS.forEach(env => {
            const apiServiceName = getApiServiceName(service.name, env);
            const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

            if (apiDist) {
              formattedDistributions.push({
                id: apiDist.id,
                service_name: service.name,
                environment: env,
                enabled: true,
                auto_publish: apiDist.auto_publish,
                subtitle_langs:
                  apiDist.subtitle_langs ||
                  (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
              });
            } else {
              formattedDistributions.push({
                service_name: service.name,
                environment: env,
                enabled: false,
                auto_publish: false,
                subtitle_langs: [],
              });
            }
          });
        });

        setDistributions(formattedDistributions);
      } else {
        initializeDefaultDistributions();
      }

      // Thumbnails 설정 (시즌의 포스터를 직접 가져오기)
      try {
        const posterList = await seasonService.fetchPoster(Number(id));
        if (posterList && posterList.length > 0) {
          const uploadedThumbnailList: ThumbnailInterface[] = posterList.map((poster: any) => ({
            ...poster,
            state: THUMBNAIL_UPLOAD_STATE.UPLOADED,
            originFileObj: undefined,
            language: normalizeLanguage(poster.language),
          }));

          const selectedThumbnail = uploadedThumbnailList.find(
            (thumbnail: ThumbnailInterface) => thumbnail.is_representative
          );

          form.setFieldValue('newThumbnailList', []);
          form.setFieldValue('uploadedThumbnailList', uploadedThumbnailList);
          form.setFieldValue('thumbnailList', uploadedThumbnailList);
          // PosterHandlers가 기대하는 필드명도 설정
          // posterList는 원본 업로드된 항목만 포함 (새로 추가한 항목 제외)
          form.setFieldValue('newPosterList', []);
          form.setFieldValue('uploadedPosterList', uploadedThumbnailList);
          form.setFieldValue('posterList', uploadedThumbnailList);
          form.setFieldValue('selectedPoster', selectedThumbnail);
          setNewThumbnailList([]);
          setUploadedThumbnailList(uploadedThumbnailList);
          setSelectedThumbnail(selectedThumbnail || null);
        }
      } catch (error) {
        console.error('Failed to fetch season posters:', error);
      }
    } catch (error) {
      console.error('Failed to fetch season:', error);
      message.error('Failed to load season data');
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await globalSettingService.fetchCountries(
        new URLSearchParams({ page_size: '1000' })
      );
      setCountries(response.results);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchWriteLanguages = async () => {
    try {
      const response = await globalSettingService.fetchLanguages(
        new URLSearchParams({
          type: 'write',
          page_size: '100',
          order_by: 'order',
          order_direction: 'asc',
        })
      );
      setWriteLanguages(response.results);
    } catch (error) {
      console.error('Failed to fetch write languages:', error);
    }
  };

  const fetchGenres = async () => {
    genreApiService
      .fetch(new URLSearchParams({ page: '1', page_size: '100' }))
      .then(response => {
        setGenreList(response.results);
      })
      .catch(error => {
        console.error('Failed to fetch genres:', error);
      });
  };

  const fetchTags = async () => {
    tagApiService
      .fetch(new URLSearchParams({ page: '1', page_size: '100' }))
      .then(response => {
        setTagList(response.results);
      })
      .catch(error => {
        console.error('Failed to fetch tags:', error);
      });
  };

  // Slug auto-generation from title en (series가 없을 때만)
  const handleTitleChange = (value: I18nFieldData) => {
    setI18nTitle(value);
    // series가 없을 때만 title로부터 slug 생성
    if (value.en && !series) {
      const autoSlug = slugify(value.en);
      form.setFieldValue('slug', autoSlug);
    }
  };

  // Season number 변경 시 slug만 업데이트 (title은 신규 생성 시에만 자동 설정, 이후 변경 안 됨)
  const handleSeasonNumberChange = (value: number | null) => {
    if (series && value) {
      // Slug만 업데이트 (title은 사용자가 입력한 값 유지)
      if (series.slug) {
        const seasonSlug = `${series.slug}-season-${value}`;
        form.setFieldValue('slug', seasonSlug);
      }
    }
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
      const formData = form.getFieldsValue();

      // title 필드 설정 (레거시 호환성)
      const title =
        i18nTitle.en || i18nTitle.ko || formData.season_title_en || formData.season_title_ko || '';
      if (!title) {
        message.error('Please enter season title (at least English or Korean)');
        return;
      }

      // genres와 tags는 form.getFieldValue를 직접 사용하여 최신 값을 가져옴
      const genresValue = form.getFieldValue('genres') || [];
      const tagsValue = form.getFieldValue('tags') || [];

      const seasonData: any = {
        ...formData,
        title: title, // 레거시 title 필드 (필수)
        title_en: i18nTitle.en || '', // 레거시 title_en 필드
        i18n_title: i18nTitle,
        i18n_synopsis: i18nSynopsis,
        i18n_seo_title: i18nSeoTitle,
        i18n_seo_synopsis: i18nSeoSynopsis,
        i18n_seo_keyword: i18nSeoKeyword,
        release_date_start: formData.release_date_start
          ? formData.release_date_start.format('YYYY-MM-DD')
          : null,
        release_date_end: formData.release_date_end
          ? formData.release_date_end.format('YYYY-MM-DD')
          : null,
        status: formData.status === 'published' ? 'published' : 'unpublished',
        enhanced_cast: enhancedCast,
        // CP from ODK, ODC, country_of_origin - undefined일 때 null로 명시적으로 설정 (서버에서 업데이트되도록)
        cp_from_odk: formData.cp_from_odk !== undefined ? formData.cp_from_odk : null,
        cp_from_odc: formData.cp_from_odc !== undefined ? formData.cp_from_odc : null,
        country_of_origin:
          formData.country_of_origin !== undefined ? formData.country_of_origin : null,
        service_auto_distribution_inherit: inherit, // inherit를 API에 전송
        service_auto_distributions: !inherit
          ? distributions
              .filter(dist => dist.enabled)
              .map(dist => ({
                service_name: getApiServiceName(dist.service_name, dist.environment),
                auto_publish: dist.auto_publish,
                subtitle_langs: dist.subtitle_langs || [],
              }))
          : [], // Inherit가 true면 빈 배열
        // genres와 tags 명시적으로 포함
        genres: genresValue || [],
        tags: tagsValue || [],
      };

      const elevenDbSeasonData: ElevenDbSeasonInterface = {
        season: Number(id),
        eleven_db_id: elevenDbSeasonId,
        awards: elevenDbAwards,
        awards_category: elevenDbAwardsCategory,
        related_series: elevenDbRelatedSeries,
        related_titles: elevenDbRelatedTitles,
        linked_articles: elevenDbLinkedArticles,
        user_ratings: elevenDbUserRatings,
        critics_ratings: elevenDbCriticsRatings,
        movie_box_office: elevenDbMovieBoxOffice,
      };

      let seasonId: number;
      if (id && id !== 'new') {
        await seasonService.updateSeasonWithI18n(Number(id), seasonData);
        await seasonService.updateElevenDbSeason(Number(id), elevenDbSeasonData);
        seasonId = Number(id);
        // 포스터 변경사항 확인 (새로 추가, 삭제, 변경)
        const posterList = form.getFieldValue('posterList') || [];
        const uploadedPosterList = form.getFieldValue('uploadedPosterList') || [];
        const hasPosterChanges =
          newThumbnailList.length > 0 ||
          removedUids.length > 0 ||
          posterList.length !== uploadedPosterList.length ||
          posterList.some(
            (p: ThumbnailInterface) =>
              !uploadedPosterList.some((up: ThumbnailInterface) => up.id === p.id)
          );

        // Upload posters if any (시즌 포스터 업로드)
        if (hasPosterChanges) {
          // originFileObj가 있는지 확인하고 merge
          const formNewPosterList = form.getFieldValue('newPosterList') || [];
          const mergedNewPosterList = formNewPosterList.map((formPoster: ThumbnailInterface) => {
            const statePoster = newThumbnailList.find(p => p.uid === formPoster.uid);
            return {
              ...formPoster,
              originFileObj: statePoster?.originFileObj || formPoster.originFileObj,
            };
          });

          // PosterHandlers가 기대하는 필드명으로 동기화
          // posterList는 원본 업로드된 항목만 포함 (변경하지 않음)
          form.setFieldValue('newPosterList', mergedNewPosterList);
          form.setFieldValue('uploadedPosterList', uploadedThumbnailList);
          // posterList는 원본 데이터로 유지 (삭제된 항목을 찾기 위해)
          form.setFieldValue('selectedPoster', selectedThumbnail);
          await handlePosterUpload();
        }

        message.success('Season updated successfully');
        window.location.reload();
      } else {
        // series_id가 있으면 설정, 없으면 undefined로 전달 (신규 series 생성 시)
        if (seriesIdParam) {
          const seriesId = Number(seriesIdParam);
          seasonData.series_id = seriesId;
          seasonData.series = seriesId; // 백엔드가 series 필드를 기대할 수도 있음 (SeriesEdit2와 동일하게)
        }
        // series_id가 없어도 season 생성 가능 (신규 series 생성 시)
        const created = await seasonService.createSeasonWithI18n(seasonData);
        await seasonService.createElevenDbSeason(created.id, {
          ...elevenDbSeasonData,
          season: Number(created.id),
        } as ElevenDbSeasonInterface);
        seasonId = created.id;

        // Upload posters if any (시즌 포스터 업로드)
        // 신규 생성 시에는 시즌 생성 후 생성된 seasonId로 posterHandlers를 다시 생성해야 함
        if (newThumbnailList.length > 0 || removedUids.length > 0) {
          // originFileObj가 있는지 확인하고 merge
          const formNewPosterList = form.getFieldValue('newPosterList') || [];
          const mergedNewPosterList = formNewPosterList.map((formPoster: ThumbnailInterface) => {
            const statePoster = newThumbnailList.find(p => p.uid === formPoster.uid);
            return {
              ...formPoster,
              originFileObj: statePoster?.originFileObj || formPoster.originFileObj,
            };
          });

          // PosterHandlers가 기대하는 필드명으로 동기화
          // posterList는 원본 업로드된 항목만 포함 (새로 추가한 항목 제외하여 삭제 대상으로 잘못 인식되지 않도록)
          form.setFieldValue('newPosterList', mergedNewPosterList);
          form.setFieldValue('uploadedPosterList', uploadedThumbnailList);
          form.setFieldValue('posterList', uploadedThumbnailList);
          form.setFieldValue('selectedPoster', selectedThumbnail);

          // 생성된 seasonId로 posterHandlers 재생성
          const newPosterHandlers = getPosterHandlers(seasonId);
          await newPosterHandlers.handlePosterUpload();
        }

        // 신규 생성 시 선택한 에피소드들을 함께 링크
        if (pendingEpisodesToLink.length > 0) {
          try {
            // unlink할 항목 제외하고 link
            const episodesToLink = pendingEpisodesToLink.filter(
              id => !pendingEpisodesToUnlink.includes(id)
            );
            if (episodesToLink.length > 0) {
              await seasonContentService.addContentsToSeason(seasonId, episodesToLink);
              message.success(`${episodesToLink.length} episode(s) linked successfully`);
            }
          } catch (error) {
            console.error('Failed to link episodes:', error);
          }
        }

        message.success('Season created successfully');
        window.location.href = `/season/edit2/${seasonId}`;
      }
    } catch (error: any) {
      console.error('Failed to save season:', error);
      message.error(error?.response?.data?.detail || 'Failed to save season');
    }
  };

  // Transfer columns for Episodes
  const transferColumns: TableColumnsType<TransferDataSourceInterface> = [
    {
      dataIndex: 'id',
      title: 'ID',
      width: '5%',
      sorter: true,
      render: (id: number) => (
        <span>
          <Button type="link" onClick={() => window.open(`/episode/edit/${id}`, '_blank')}>
            {id}
          </Button>
        </span>
      ),
    },
    {
      dataIndex: 'poster',
      title: 'Thumbnail',
      width: '10%',
      render: (_text: string, record: TransferDataSourceInterface) => {
        const posterUrl = record.poster?.url;
        if (!posterUrl) {
          return <div>No Thumbnail</div>;
        }
        return <Image src={`${posterUrl}`} width={100} height={100} />;
      },
    },
    {
      dataIndex: 'name',
      width: '60%',
      title: 'Title En',
      sorter: true,
    },
    {
      dataIndex: 'episode_order',
      width: '5%',
      title: 'number',
      sorter: true,
    },
    {
      dataIndex: 'content_type',
      width: '5%',
      title: 'type',
    },
    // {
    //   dataIndex: 'status',
    //   width: '5%',
    //   title: 'Status',
    //   render: (status: string) => {
    //     const statusMap: Record<string, { color: string; text: string }> = {
    //       published: { color: 'green', text: 'Enabled' },
    //       unpublished: { color: 'default', text: 'Disabled' },
    //     };
    //     const statusInfo = statusMap[status] || { color: 'default', text: status };
    //     return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
    //   },
    // },
  ];

  // Episode Transfer Data Handler
  const episodeDataHandler: TransferDataHandlerInterface = {
    fetchSourceData: async (
      query: Record<string, string>
    ): Promise<{ data: TransferDataSourceInterface[]; count: number }> => {
      const queryParams = new URLSearchParams(query);
      // order_by가 있으면 사용, 없으면 기본값 'id' 사용
      // 'name' 필드는 'title'로 매핑
      const orderBy = query.order_by || 'id';
      const mappedOrderBy = orderBy === 'name' ? 'title' : orderBy;
      queryParams.set('order_by', mappedOrderBy);
      // order_direction이 있으면 사용, 없으면 기본값 'desc' 사용
      queryParams.set('order_direction', query.order_direction || 'desc');
      const response = await episodeService.fetch(queryParams);
      const transferDataSource = response.results.map((episode: EpisodeInterface) => {
        const status = episode.status
          ? episode.status === 'published'
            ? 'Enabled'
            : 'Disabled'
          : 'Disabled';
        const data: TransferDataSourceInterface & {
          episode_order?: number;
          content_type?: string;
          status?: string;
          poster?: PosterInterface;
        } = {
          id: episode.id || 0,
          key: episode.id || 0,
          name: episode.i18n_title?.en || (episode as any).title_en || '<Edit title here>',
          disabled: false,
          episode_order: (episode as any).episode_order || 0,
          content_type: (episode as any).content_type || 'episode',
          status: status,
          poster: episode.poster || undefined,
        };
        // 신규 생성 시 선택한 에피소드 데이터 캐시
        if ((!id || id === 'new') && episode.id) {
          setPendingEpisodesData(prev =>
            new Map(prev).set(episode.id!, data as TransferDataSourceInterface)
          );
        }
        return data;
      });
      return {
        data: transferDataSource,
        count: response.count || 0,
      };
    },
    fetchTargetData: async (
      query: Record<string, string>
    ): Promise<{ data: TransferDataSourceInterface[]; count: number }> => {
      if (!id || id === 'new') {
        // 신규 생성 시 pendingEpisodesToLink에 있는 항목들을 반환
        const pendingIds = pendingEpisodesToLink.filter(
          id => !pendingEpisodesToUnlink.includes(id)
        );
        const pendingData = pendingIds
          .map(episodeId => pendingEpisodesData.get(episodeId))
          .filter((data): data is TransferDataSourceInterface => data !== undefined);

        // 검색 필터링
        let filteredData = pendingData;
        if (query.search) {
          const searchTerm = query.search.toLowerCase();
          filteredData = filteredData.filter(data => data.name.toLowerCase().includes(searchTerm));
        }

        // 상태로 필터링
        if (query.status) {
          filteredData = filteredData.filter(data => (data as any).status === query.status);
        }

        return {
          data: filteredData,
          count: filteredData.length,
        };
      }
      try {
        const queryParams = new URLSearchParams();
        // order_by가 있으면 사용, 없으면 기본값 'episode_order' 사용
        // 'name' 필드는 'title'로 매핑
        const orderBy = query.order_by || 'episode_order';
        const mappedOrderBy = orderBy === 'name' ? 'title' : orderBy;
        queryParams.set('order_by', mappedOrderBy);
        // order_direction이 있으면 사용, 없으면 기본값 'desc' 사용
        queryParams.set('order_direction', query.order_direction || 'desc');

        const seasonContents = await seasonContentService.getSeasonContents(Number(id), queryParams);
        let filteredContents = seasonContents.contents;

        // clip/trailer 타입은 has_additional_link가 true인 것만 표시
        filteredContents = filteredContents.filter(content => {
          if (content.content_type === 'clip' || content.content_type === 'trailer') {
            return content.has_additional_link === true;
          }
          return true;
        });

        // 검색 필터링
        if (query.search) {
          const searchTerm = query.search.toLowerCase();
          filteredContents = filteredContents.filter(
            content =>
              content.title.toLowerCase().includes(searchTerm) ||
              content.title_en?.toLowerCase().includes(searchTerm)
          );
        }

        // 상태로 필터링
        if (query.status) {
          filteredContents = filteredContents.filter(content => content.status === query.status);
        }

        return {
          data: filteredContents.map((content: SeasonContentInterface) => {
            const status = content.status
              ? content.status === 'published'
                ? 'Enabled'
                : 'Disabled'
              : 'Disabled';
            return {
              id: content.id,
              key: content.id,
              name: content.title_en,
              content_type: content.content_type,
              episode_order: content.episode_order,
              disabled: false,
              status: status,
              poster: content.poster || undefined,
            };
          }),
          count: filteredContents.length,
        };
      } catch (error) {
        console.error('Season content 조회 실패:', error);
        return { data: [], count: 0 };
      }
    },
    createWithParentId: async (itemIds: number[]): Promise<void> => {
      if (!id || id === 'new') {
        // 신규 생성 시에는 실제 API 호출 대신 pending 상태에 저장
        setPendingEpisodesToLink(prev => {
          const newIds = itemIds.filter(id => !prev.includes(id));
          return [...prev, ...newIds];
        });
        setPendingEpisodesToUnlink(prev => prev.filter(id => !itemIds.includes(id)));
        return;
      }
      try {
        for (const itemId of itemIds) {
          const episode = await episodeService.fetchById(itemId);
          const i18nTitles: I18nFieldData = {};
          for (const lang in i18nTitle) {
            i18nTitles[lang as keyof I18nFieldData] =
              `${i18nTitle[lang as keyof I18nFieldData]} : ${episode.episode_order}`;
          }
          await episodeService.update(itemId, form, {
            i18n_title: i18nTitles,
          } as Partial<EpisodeInterface>);
        }
        await seasonContentService.addContentsToSeason(Number(id), itemIds);
        // Total # of Episodes 업데이트
        try {
          const seasonContents = await seasonContentService.getSeasonContents(Number(id));
          const episodeCount = seasonContents.contents.filter(
            content => content.content_type === 'episode'
          ).length;
          form.setFieldValue('total_episodes', episodeCount);
        } catch (error) {
          console.error('Failed to update episode count:', error);
        }
      } catch (error) {
        console.error('Season에 content 추가 실패:', error);
        throw error;
      }
    },
    deleteWithParentId: async (itemIds: number[]): Promise<void> => {
      if (!id || id === 'new') {
        // 신규 생성 시에는 실제 API 호출 대신 pending 상태에 저장
        setPendingEpisodesToUnlink(prev => {
          const newIds = itemIds.filter(id => !prev.includes(id));
          return [...prev, ...newIds];
        });
        setPendingEpisodesToLink(prev => prev.filter(id => !itemIds.includes(id)));
        return;
      }
      try {
        await seasonContentService.removeContentsFromSeason(Number(id), itemIds);
        // Total # of Episodes 업데이트
        try {
          const seasonContents = await seasonContentService.getSeasonContents(Number(id));
          const episodeCount = seasonContents.contents.filter(
            content => content.content_type === 'episode'
          ).length;
          form.setFieldValue('total_episodes', episodeCount);
        } catch (error) {
          console.error('Failed to update episode count:', error);
        }
      } catch (error) {
        console.error('Season에서 content 제거 실패:', error);
        throw error;
      }
    },
  };

  // Genres & Tags tabs for CustomSelector
  const tabs = useMemo<CustomSelectorTabInterface<any>[]>(() => {
    const genreTab: CustomSelectorTabInterface<GenreInterface> = {
      label: 'Genre',
      selected: genreList.filter(g => {
        // 백엔드에서 genres 또는 categories로 올 수 있음, 폼에서는 genres로 사용
        const genreIds = form.getFieldValue('genres') || (season as any)?.genres || [];
        return genreIds.includes(g.id);
      }),
      setSelected: (selected: GenreInterface[]) => {
        const genreIds = selected.map(g => g.id);
        form.setFieldValue('genres', genreIds);
        setGenreList(selected);
        markFieldChanged('genres');
      },
      fetchSearch: (search: string) =>
        genreApiService
          .fetch(new URLSearchParams({ search, page: '1', page_size: '100' }))
          .then(res => res.results),
      getLabel: (genre: GenreInterface) => genre.slug || genre.i18n_name?.ko || '',
    };

    const tagTab: CustomSelectorTabInterface<TagInterface> = {
      label: 'Tag',
      selected: tagList.filter(t => {
        const tagIds = form.getFieldValue('tags') || (season as any)?.tags || [];
        return tagIds.includes(t.id);
      }),
      setSelected: (selected: TagInterface[]) => {
        const tagIds = selected.map(t => t.id);
        form.setFieldValue('tags', tagIds);
        setTagList(selected);
        markFieldChanged('tags');
      },
      fetchSearch: (search: string) =>
        tagApiService
          .fetch(new URLSearchParams({ search, page: '1', page_size: '100' }))
          .then(res => res.results),
      getLabel: (tag: TagInterface) => tag.name || tag.i18n_name?.ko || '',
    };

    return [genreTab, tagTab];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genreList, tagList, season]);

  // Grouped thumbnails by language
  const allThumbnails = useMemo(
    () => [...newThumbnailList, ...uploadedThumbnailList],
    [newThumbnailList, uploadedThumbnailList]
  );

  const groupedByLanguage = useMemo(() => {
    const grouped: Record<string, ThumbnailInterface[]> = {};
    POSTER_LANGUAGE_OPTIONS.forEach(lang => {
      grouped[lang.value] = allThumbnails.filter(
        thumb => normalizeLanguage(thumb.language) === lang.value
      );
    });
    return grouped;
  }, [allThumbnails]);

  // Get thumbnail by language and orientation
  const getThumbnailByLanguageAndOrientation = (
    language: string,
    orientation: string
  ): ThumbnailInterface | null => {
    const langThumbnails = groupedByLanguage[normalizeLanguage(language)] || [];
    return (
      langThumbnails.find(
        thumb => thumb.orientation === orientation || thumb.orientation === orientation
      ) || null
    );
  };

  // Search people for enhanced cast
  const searchPeopleForEnhancedCast = async (value: string): Promise<void> => {
    if (!value.trim()) return;
    const response = await peopleApiService.fetch(
      new URLSearchParams({
        page: '1',
        page_size: '20',
        search: value,
      })
    );
    setSearchedPeopleList(response.results);
  };

  // Distribution admin 링크 열기
  const handleOpenDistributionAdmin = (
    _seasonId: number,
    programId: number,
    serviceName: string
  ) => {
    const hostMap: Record<string, string> = {
      'odc-staging': 'https://admin.staging2.odkmedia.net/odc',
      odc: 'https://admin.odkmedia.net/odc',
      'odk-staging': 'https://admin.staging2.odkmedia.net/odk',
      odk: 'https://admin.odkmedia.net/odk',
      'amasian-staging': 'https://amasian-admin.staging2.odkmedia.net',
      amasian: 'https://amasian-admin.odkmedia.net',
    };
    if (serviceName === 'amasian' || serviceName === 'amasian-staging') {
      const url = `${hostMap[serviceName]}/season/edit/${programId}`;
      window.open(url, '_blank');
    } else if (serviceName === 'odc' || serviceName === 'odc-staging') {
      const url = `${hostMap[serviceName]}/content/program/${programId}`;
      window.open(url, '_blank');
    } else if (serviceName === 'odk' || serviceName === 'odk-staging') {
      const url = `${hostMap[serviceName]}/content/program/${programId}`;
      window.open(url, '_blank');
    } else {
      const url = `${hostMap[serviceName]}/content/program/${programId}`;
      window.open(url, '_blank');
    }
  };

  const handleEpisodeOrderChange = async (orders: { id: number; [key: string]: number }[]) => {
    try {
      // 배치 업데이트 포맷으로 변환
      const updates = orders.map(order => {
        const orderField = Object.keys(order).find(key => key !== 'id');
        return {
          content_id: order.id,
          // eslint-disable-next-line camelcase
          episode_order: orderField ? order[orderField] : 0,
        };
      });

      await episodeService.batchUpdateOrder(updates);
      message.success('Episode order updated successfully');
    } catch (error) {
      message.error('Failed to update episode order');
      console.error(error);
    }
  };

  // Series의 distribution을 포맷팅하는 함수 추가
  const formatSeriesDistributions = useMemo(() => {
    if (!series || !(series as any).service_auto_distributions) {
      return createDefaultDistributions();
    }

    const apiDistributions = (series as any).service_auto_distributions;
    const formattedDistributions: any[] = [];

    SERVICE_OPTIONS.forEach(service => {
      ENVIRONMENTS.forEach(env => {
        const apiServiceName = getApiServiceName(service.name, env);
        const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

        if (apiDist) {
          formattedDistributions.push({
            id: apiDist.id,
            service_name: service.name,
            environment: env,
            enabled: true,
            auto_publish: apiDist.auto_publish,
            subtitle_langs:
              apiDist.subtitle_langs ||
              (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
          });
        } else {
          formattedDistributions.push({
            service_name: service.name,
            environment: env,
            enabled: false,
            auto_publish: false,
            subtitle_langs: [],
          });
        }
      });
    });

    return formattedDistributions;
  }, [series]);

  return (
    <Component.BasePageComponent>
      <div style={{ padding: '24px' }}>
        <Form
          form={form}
          layout="vertical"
          className="season-edit2-form"
          onValuesChange={handleValuesChange}
        >
          {/* Admin Links */}
          <div className="admin-links-container">
            <div
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: '#fffbe6',
                borderRadius: '4px',
                marginBottom: '16px',
              }}
            >
              <Space>
                {season?.distribution_sets && season.distribution_sets.length > 0 ? (
                  season.distribution_sets.map((dist, index) => (
                    <Button
                      key={index}
                      size="small"
                      onClick={() =>
                        handleOpenDistributionAdmin(
                          dist.season_id,
                          dist.program_id,
                          dist.service_name
                        )
                      }
                    >
                      {dist.service_name}
                    </Button>
                  ))
                ) : (
                  <div style={{ color: '#999' }}>No Distribution</div>
                )}
              </Space>
            </div>
          </div>

          {/* Season Metadata Section */}
          <Card title="Season Metadata" style={{ marginBottom: '24px' }}>
            {/* Series Title (readonly) */}
            {series && series.id && (
              <div style={{ marginBottom: '16px' }}>
                <Form.Item
                  label={
                    <div>
                      Series Slug{' '}
                      <Button
                        type="link"
                        onClick={() => window.open(`/series/edit2/${series.id}`, '_blank')}
                      >
                        Open Link
                      </Button>
                    </div>
                  }
                  style={{ marginBottom: 0 }}
                >
                  <Input.Group compact>
                    <Input readOnly value={series.slug} style={{ width: 'calc(100% - 100px)' }} />
                  </Input.Group>
                </Form.Item>
              </div>
            )}

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label={
                  series
                    ? 'Season Slug (auto-gen from series slug and season number)*'
                    : 'Season Slug (auto-gen from title en)*'
                }
                name="slug"
                style={{ flex: 1, marginBottom: 0 }}
                rules={[{ required: true, message: 'Please enter a slug' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Season Number"
                name="season_order"
                style={{ flex: 1, marginBottom: 0 }}
                rules={[{ required: true, message: 'Please enter season number' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Default rule if create N+1, where N is the largest season number in series"
                  onChange={handleSeasonNumberChange}
                />
              </Form.Item>
            </div>

            {/* Season Titles */}
            <div style={{ marginBottom: '16px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `Season Title ${lang.rfc_code}`,
                  children: (
                    <Input
                      value={(i18nTitle as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        handleTitleChange({
                          ...i18nTitle,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="Total # of Episodes"
                name="total_episodes"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
              <Form.Item
                label="Age Rating (Default TV-G)"
                name="age_rating"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select>
                  {AGE_RATING.map(rating => (
                    <Select.Option key={rating} value={rating}>
                      {rating}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="Country of Origin"
                name="country_of_origin"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select country">
                  {countries.map(country => (
                    <Select.Option key={country.id} value={country.id}>
                      {country.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Running Time"
                name="running_time"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <CustomInputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </div>

            {/* cp from odk, cp from odc */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="CP from ODK"
                name="cp_from_odk"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Search CP from ODK..."
                  filterOption={(input, option) =>
                    (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={cpFromOdkOptions}
                />
              </Form.Item>

              <Form.Item
                label="CP from ODC"
                name="cp_from_odc"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Search CP from ODC..."
                  filterOption={false}
                  onSearch={handleCpFromOdcSearch}
                  onClear={handleCpFromOdcClear}
                  loading={cpFromOdcLoading}
                  notFoundContent={cpFromOdcLoading ? 'Searching...' : 'No CP found'}
                  onDropdownVisibleChange={handleCpFromOdcDropdownVisibleChange}
                  options={cpFromOdcOptions}
                />
              </Form.Item>

              <Form.Item
                label="CP for third option"
                name="cp_from_odx"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Input />
              </Form.Item>
            </div>

            {/* Synopsis Tabs */}
            <div style={{ marginBottom: '16px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `Synopsis ${lang.rfc_code}`,
                  children: (
                    <Input.TextArea
                      rows={6}
                      value={(i18nSynopsis as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSynopsis({
                          ...i18nSynopsis,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
            </div>
          </Card>

          {/* Date and Release Weekday Section */}
          <Card title="Date and Release Weekday" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="Season released date"
                name="release_date_start"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                  onChange={date => {
                    if (date) {
                      const year = date.year();
                      form.setFieldValue('production_year', year);
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Season ended date"
                name="release_date_end"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item
                label="Production Year"
                name="production_year"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <CustomInputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </div>

            <Form.Item label="Release Weekday" name="release_weekday">
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.release_weekday !== currentValues.release_weekday
                }
              >
                {() => {
                  const releaseWeekday = form.getFieldValue('release_weekday') || '';
                  const checkedValues = releaseWeekday
                    ? releaseWeekday.split(',').filter((v: string) => v.trim())
                    : [];
                  return (
                    <Space wrap>
                      {WEEKDAYS.map(day => (
                        <Checkbox
                          key={day.value}
                          checked={checkedValues.includes(day.value)}
                          onChange={e => {
                            const current = form.getFieldValue('release_weekday') || '';
                            const currentValues = current
                              ? current.split(',').filter((v: string) => v.trim())
                              : [];
                            const newValues = e.target.checked
                              ? [...currentValues, day.value]
                              : currentValues.filter((v: string) => v !== day.value);
                            form.setFieldValue('release_weekday', newValues.join(','));
                          }}
                        >
                          {day.short}
                        </Checkbox>
                      ))}
                    </Space>
                  );
                }}
              </Form.Item>
            </Form.Item>
          </Card>

          {/* Series Posters Section */}
          <Card title="Season Posters" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
              {POSTER_LANGUAGE_OPTIONS.map(lang => {
                const landscapeThumb = getThumbnailByLanguageAndOrientation(
                  lang.value,
                  'landscape'
                );
                const portraitThumb = getThumbnailByLanguageAndOrientation(lang.value, 'portrait');
                const squareThumb = getThumbnailByLanguageAndOrientation(lang.value, 'square');
                const langCleanThumb = getThumbnailByLanguageAndOrientation(
                  lang.value,
                  'lang_clean'
                );

                return (
                  <div key={lang.value}>
                    <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>{lang.label}</div>

                    {/* Portrait 880 x 1280 */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                        Poster Portrait (880 x 1280)
                      </div>
                      {portraitThumb ? (
                        <div
                          style={{
                            width: '100%',
                            aspectRatio: '880/1280',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            position: 'relative',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (portraitThumb?.file) {
                              window.open(portraitThumb.file, '_blank');
                            }
                          }}
                        >
                          <img
                            src={portraitThumb.file || ''}
                            alt="portrait"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                          <Button
                            type="text"
                            danger
                            icon={<span>×</span>}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              padding: 0,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              handlePosterRemove(portraitThumb);
                            }}
                          />
                        </div>
                      ) : (
                        <Upload
                          accept="image/*,.psd"
                          showUploadList={false}
                          beforeUpload={() => false}
                          onChange={handleUploadWithLanguageAndOrientation(lang.value, 'portrait')}
                          style={{ width: '100%', display: 'block' }}
                        >
                          <div
                            style={{
                              width: '100%',
                              aspectRatio: '880/1280',
                              border: '1px dashed #d9d9d9',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              cursor: 'pointer',
                            }}
                          >
                            <span style={{ color: '#999', fontSize: '12px' }}>
                              Click empty imageholder to browse image files and upload.
                            </span>
                          </div>
                        </Upload>
                      )}
                    </div>

                    {/* Landscape 1600x900 */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                        Poster Landscape (1600 x 900)
                      </div>
                      {landscapeThumb ? (
                        <div
                          style={{
                            width: '100%',
                            aspectRatio: '16/9',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            position: 'relative',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (landscapeThumb?.file) {
                              window.open(landscapeThumb.file, '_blank');
                            }
                          }}
                        >
                          <img
                            src={landscapeThumb.file || ''}
                            alt="landscape"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                          <Button
                            type="text"
                            danger
                            icon={<span>×</span>}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              padding: 0,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              handlePosterRemove(landscapeThumb);
                            }}
                          />
                        </div>
                      ) : (
                        <Upload
                          accept="image/*,.psd"
                          showUploadList={false}
                          beforeUpload={() => false}
                          onChange={handleUploadWithLanguageAndOrientation(lang.value, 'landscape')}
                          style={{ width: '100%', display: 'block' }}
                        >
                          <div
                            style={{
                              width: '100%',
                              aspectRatio: '16/9',
                              border: '1px dashed #d9d9d9',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              cursor: 'pointer',
                            }}
                          >
                            <span style={{ color: '#999', fontSize: '12px' }}>
                              Click empty imageholder to browse image files and upload.
                            </span>
                          </div>
                        </Upload>
                      )}
                    </div>

                    {/* Lang Clean 1600x900 */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                        Clean (1600 x 900)
                      </div>
                      {langCleanThumb ? (
                        <div
                          style={{
                            width: '100%',
                            aspectRatio: '16/9',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            position: 'relative',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (langCleanThumb?.file) {
                              window.open(langCleanThumb.file, '_blank');
                            }
                          }}
                        >
                          <img
                            src={langCleanThumb.file || ''}
                            alt="lang clean"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                          <Button
                            type="text"
                            danger
                            icon={<span>×</span>}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              padding: 0,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              handlePosterRemove(langCleanThumb);
                            }}
                          />
                        </div>
                      ) : (
                        <Upload
                          accept="image/*,.psd"
                          showUploadList={false}
                          beforeUpload={() => false}
                          onChange={handleUploadWithLanguageAndOrientation(
                            lang.value,
                            'lang_clean'
                          )}
                          style={{ width: '100%', display: 'block' }}
                        >
                          <div
                            style={{
                              width: '100%',
                              aspectRatio: '16/9',
                              border: '1px dashed #d9d9d9',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              cursor: 'pointer',
                            }}
                          >
                            <span style={{ color: '#999', fontSize: '12px' }}>
                              Click empty imageholder to browse image files and upload.
                            </span>
                          </div>
                        </Upload>
                      )}
                    </div>

                    {/* Square 1080x1080 */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                        Square (1080 x 1080)
                      </div>
                      <div
                        style={{
                          padding: '8px',
                          backgroundColor: '#fffbe6',
                          borderRadius: '4px',
                          fontSize: '12px',
                          color: '#666',
                          marginBottom: '4px',
                        }}
                      >
                        1:1 not supported yet. required for Plex distribution.
                      </div>
                      {squareThumb ? (
                        <div
                          style={{
                            width: '100%',
                            aspectRatio: '1/1',
                            border: '1px dashed #d9d9d9',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            position: 'relative',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (squareThumb?.file) {
                              window.open(squareThumb.file, '_blank');
                            }
                          }}
                        >
                          <img
                            src={squareThumb.file || ''}
                            alt="square"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                          <Button
                            type="text"
                            danger
                            icon={<span>×</span>}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              padding: 0,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              handlePosterRemove(squareThumb);
                            }}
                          />
                        </div>
                      ) : (
                        <Upload
                          accept="image/*,.psd"
                          showUploadList={false}
                          beforeUpload={() => false}
                          onChange={handleUploadWithLanguageAndOrientation(lang.value, 'square')}
                          style={{ width: '100%', display: 'block' }}
                        >
                          <div
                            style={{
                              width: '100%',
                              aspectRatio: '1/1',
                              border: '1px dashed #d9d9d9',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              cursor: 'pointer',
                            }}
                          >
                            <span style={{ color: '#999', fontSize: '12px' }}>
                              Click empty imageholder to browse image files and upload.
                            </span>
                          </div>
                        </Upload>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {id && id !== 'new' && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Button
                  type="primary"
                  onClick={async () => {
                    // originFileObj가 있는지 확인하고 merge
                    const formNewPosterList = form.getFieldValue('newPosterList') || [];
                    const mergedNewPosterList = formNewPosterList.map(
                      (formPoster: ThumbnailInterface) => {
                        const statePoster = newThumbnailList.find(p => p.uid === formPoster.uid);
                        return {
                          ...formPoster,
                          originFileObj: statePoster?.originFileObj || formPoster.originFileObj,
                        };
                      }
                    );

                    // PosterHandlers가 기대하는 필드명으로 동기화
                    // posterList는 원본 업로드된 항목만 포함 (변경하지 않음)
                    form.setFieldValue('newPosterList', mergedNewPosterList);
                    form.setFieldValue('uploadedPosterList', uploadedThumbnailList);
                    // posterList는 원본 데이터로 유지 (삭제된 항목을 찾기 위해)
                    form.setFieldValue('selectedPoster', selectedThumbnail);
                    await handlePosterUpload();
                  }}
                >
                  Save Posters
                </Button>
              </div>
            )}
            {(!id || id === 'new') && (
              <div
                style={{
                  marginTop: '16px',
                  padding: '8px',
                  backgroundColor: '#fffbe6',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#666',
                  textAlign: 'center',
                }}
              >
                Posters will be saved when you save the season
              </div>
            )}
          </Card>

          {/* Episode Management Section */}
          <Card title="Episode Management" style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <Button
                  type="primary"
                  onClick={() => {
                    const seasonData = form.getFieldsValue();
                    const seriesIdFromSeason =
                      (seasonData as any).series_id || (seasonData as any).series;
                    const seriesId =
                      seriesIdParam ||
                      (series?.id ? String(series.id) : '') ||
                      (seriesIdFromSeason ? String(seriesIdFromSeason) : '');

                    if (!id || id === 'new') {
                      // 시즌이 아직 저장되지 않은 경우, 현재 폼 데이터를 sessionStorage에 저장
                      const seasonFormData = {
                        i18n_title: i18nTitle,
                        i18n_seo_title: i18nSeoTitle,
                        i18n_seo_synopsis: i18nSeoSynopsis,
                        i18n_seo_keyword: i18nSeoKeyword,
                        service_auto_distribution_inherit: inherit,
                        service_auto_distributions: distributions,
                        series_id: seriesId ? Number(seriesId) : undefined,
                      };
                      sessionStorage.setItem('pendingSeasonData', JSON.stringify(seasonFormData));
                      const url = seriesId
                        ? `/episode/edit/new?series_id=${seriesId}&season_id=pending`
                        : `/episode/edit/new?season_id=pending`;
                      window.open(url, '_blank');
                    } else {
                      // 시즌이 이미 저장된 경우 기존대로 처리
                      const url = seriesId
                        ? `/episode/edit/new?series_id=${seriesId}&season_id=${id}`
                        : `/episode/edit/new?season_id=${id}`;
                      window.open(url, '_blank');
                    }
                  }}
                >
                  Create Episodes/Trailers
                </Button>
              </div>
            </div>
            <Component.TransferComponent
              dataHandler={episodeDataHandler}
              columns={transferColumns}
              // disableUnlink={true}
              orderOptions={{
                fieldName: 'episode_order',
                orderByField: 'episode_order',
                onOrderChange: handleEpisodeOrderChange,
              }}
            />
          </Card>

          {/* SEO Information Section */}
          <Card title="SEO Information" style={{ marginBottom: '24px' }}>
            {/* SEO Title */}
            <div style={{ marginBottom: '24px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `SEO title ${lang.rfc_code}`,
                  children: (
                    <Input
                      value={(i18nSeoTitle as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSeoTitle({
                          ...i18nSeoTitle,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                      }}
                    />
                  ),
                }))}
              />
            </div>

            <Divider />

            {/* SEO Keyword */}
            <div style={{ marginBottom: '24px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `SEO keyword ${lang.rfc_code}`,
                  children: (
                    <Input
                      value={(i18nSeoKeyword as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSeoKeyword({
                          ...i18nSeoKeyword,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                      }}
                    />
                  ),
                }))}
              />
            </div>

            <Divider />

            {/* SEO Synopsis */}
            <div style={{ marginBottom: '24px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `SEO Synopsis ${lang.rfc_code}`,
                  children: (
                    <Input.TextArea
                      rows={6}
                      value={(i18nSeoSynopsis as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSeoSynopsis({
                          ...i18nSeoSynopsis,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                      }}
                    />
                  ),
                }))}
              />
            </div>
          </Card>

          {/* Enhanced Cast Section */}
          <Card title="Enhanced Cast" style={{ marginBottom: '24px' }}>
            <EnhancedCastEditor
              value={enhancedCast}
              onChange={setEnhancedCast}
              peopleList={searchedPeopleList}
              onSearchPeople={searchPeopleForEnhancedCast}
            />
          </Card>

          {/* Genres & Tags Section */}
          <Card title="Genres & Tags" style={{ marginBottom: '24px' }}>
            <Form.Item>
              <Form.Item name="genres" hidden />
              <Form.Item name="tags" hidden />
              <CustomSelector tabs={tabs} />
            </Form.Item>
          </Card>

          {/* 11DB Metadata Section */}
          <Card
            title={`11DB Metadata ${elevenDbSeasonId ? `{${elevenDbSeasonId}}` : ''}`}
            style={{ marginBottom: '24px' }}
          >
            <Form.Item label="11DB Series ID">
              <Input
                value={elevenDbSeasonId}
                onChange={e => setElevenDbSeasonId(e.target.value)}
                placeholder="Enter 11DB series ID"
              />
            </Form.Item>

            <Form.Item label="Awards">
              <Input
                style={{ width: '100%' }}
                placeholder="Enter award name"
                value={elevenDbAwards}
                onChange={e => {
                  setElevenDbAwards(e.target.value);
                }}
              />
            </Form.Item>

            <Form.Item label="Awards Category">
              <Input
                placeholder="Enter award category"
                value={elevenDbAwardsCategory}
                onChange={e => setElevenDbAwardsCategory(e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Related Series">
              <Input
                placeholder="Enter series ID or name"
                value={elevenDbRelatedSeries}
                onChange={e => setElevenDbRelatedSeries(e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Related Titles">
              <Input
                placeholder="Enter title ID or name"
                value={elevenDbRelatedTitles}
                onChange={e => setElevenDbRelatedTitles(e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Linked Articles">
              <Input
                placeholder="Enter article URL"
                value={elevenDbLinkedArticles}
                onChange={e => setElevenDbLinkedArticles(e.target.value)}
              />
            </Form.Item>
            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item label="User Ratings">
                <Input
                  placeholder="Enter user ratings"
                  value={elevenDbUserRatings}
                  onChange={e => setElevenDbUserRatings(e.target.value)}
                />
              </Form.Item>

              <Form.Item label="Critics Ratings">
                <Input
                  placeholder="Enter critics ratings"
                  value={elevenDbCriticsRatings}
                  onChange={e => setElevenDbCriticsRatings(e.target.value)}
                />
              </Form.Item>

              <Form.Item label="Movie Box Office">
                <Input
                  placeholder="Enter movie box office"
                  value={elevenDbMovieBoxOffice}
                  onChange={e => setElevenDbMovieBoxOffice(e.target.value)}
                />
              </Form.Item>
            </div>
          </Card>

          {/* Service Auto Distribution Section */}
          <Card title="Service Auto Distribution" style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '24px' }}>
              <Checkbox checked={inherit} onChange={e => setInherit(e.target.checked)}>
                <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Inherit from Series</span>
              </Checkbox>
            </div>

            {!inherit ? (
              <>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                  Service Auto Distribution for the season.
                </p>

                <Table
                  dataSource={(() => {
                    const data: any[] = [];
                    // // Divider 추가
                    // data.push({
                    //   key: 'divider',
                    //   isDivider: true,
                    // });

                    if (process.env.REACT_APP_ENV == 'production') {
                      // Production 서비스 추가
                      SERVICE_OPTIONS.forEach(service => {
                        const dist = distributions.find(
                          d => d.service_name === service.name && d.environment === 'production'
                        );
                        data.push({
                          key: `${service.name}-production`,
                          service: service.name,
                          environment: 'production',
                          dist: dist || {
                            service_name: service.name,
                            environment: 'production',
                            enabled: false,
                            auto_publish: false,
                            subtitle_langs: [],
                          },
                        });
                      });
                    } else {
                      // Staging 서비스 추가
                      SERVICE_OPTIONS.forEach(service => {
                        const dist = distributions.find(
                          d => d.service_name === service.name && d.environment === 'staging'
                        );
                        data.push({
                          key: `${service.name}-staging`,
                          service: service.name,
                          environment: 'staging',
                          dist: dist || {
                            service_name: service.name,
                            environment: 'staging',
                            enabled: false,
                            auto_publish: false,
                            subtitle_langs: [],
                          },
                        });
                      });
                    }

                    return data;
                  })()}
                  columns={[
                    {
                      title: 'Service',
                      dataIndex: 'service',
                      key: 'service',
                      width: '320px',
                      render: (text: string, record: any) => {
                        if (record.isDivider) {
                          return {
                            children: <div style={{ height: '1px', background: '#e8e8e8' }} />,
                            props: { colSpan: 3 },
                          };
                        }
                        return (
                          <Checkbox
                            checked={record.dist.enabled || false}
                            onChange={e => {
                              updateDistribution(record.service, record.environment, {
                                enabled: e.target.checked,
                              });
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {text} (
                              {record.environment === 'production' ? 'Production' : 'Staging'})
                            </span>
                          </Checkbox>
                        );
                      },
                    },
                    {
                      title: 'Auto Publish',
                      key: 'auto_publish',
                      width: '150px',
                      render: (_: any, record: any) => {
                        if (record.isDivider) {
                          return { props: { colSpan: 0 } };
                        }
                        return (
                          <Checkbox
                            checked={record.dist.auto_publish || false}
                            disabled={!record.dist.enabled}
                            onChange={e =>
                              updateDistribution(record.service, record.environment, {
                                auto_publish: e.target.checked,
                              })
                            }
                          >
                            Auto Publish
                          </Checkbox>
                        );
                      },
                    },
                    {
                      title: 'Subtitles',
                      key: 'subtitles',
                      render: (_: any, record: any) => {
                        if (record.isDivider) {
                          return { props: { colSpan: 0 } };
                        }
                        return (
                          <Space wrap>
                            {SUBTITLE_LANGUAGES.map(lang => (
                              <Checkbox
                                key={lang.value}
                                checked={record.dist.subtitle_langs?.includes(lang.value) || false}
                                disabled={!record.dist.enabled}
                                onChange={e => {
                                  const newSubtitles = e.target.checked
                                    ? [...(record.dist.subtitle_langs || []), lang.value]
                                    : (record.dist.subtitle_langs || []).filter(
                                        (s: string) => s !== lang.value
                                      );
                                  updateDistribution(record.service, record.environment, {
                                    subtitle_langs: newSubtitles,
                                  });
                                }}
                              >
                                {lang.label}
                              </Checkbox>
                            ))}
                          </Space>
                        );
                      },
                    },
                  ]}
                  pagination={false}
                  bordered
                />
              </>
            ) : (
              <>
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                  Inherit Service Auto Distribution from Series.
                </p>
                <Table
                  dataSource={(() => {
                    const data: any[] = [];
                    // Divider 추가
                    data.push({
                      key: 'divider',
                      isDivider: true,
                    });

                    // Series의 distribution 사용
                    const seriesDistributions = formatSeriesDistributions;

                    if (process.env.REACT_APP_ENV == 'production') {
                      // Production 서비스 추가
                      SERVICE_OPTIONS.forEach(service => {
                        const dist = seriesDistributions.find(
                          d => d.service_name === service.name && d.environment === 'production'
                        );
                        data.push({
                          key: `${service.name}-production`,
                          service: service.name,
                          environment: 'production',
                          dist: dist || {
                            service_name: service.name,
                            environment: 'production',
                            enabled: false,
                            auto_publish: false,
                            subtitle_langs: [],
                          },
                        });
                      });
                    } else {
                      // Staging 서비스 추가
                      SERVICE_OPTIONS.forEach(service => {
                        const dist = seriesDistributions.find(
                          d => d.service_name === service.name && d.environment === 'staging'
                        );
                        data.push({
                          key: `${service.name}-staging`,
                          service: service.name,
                          environment: 'staging',
                          dist: dist || {
                            service_name: service.name,
                            environment: 'staging',
                            enabled: false,
                            auto_publish: false,
                            subtitle_langs: [],
                          },
                        });
                      });
                    }

                    return data;
                  })()}
                  columns={[
                    {
                      title: 'Service',
                      dataIndex: 'service',
                      key: 'service',
                      width: '320px',
                      render: (text: string, record: any) => {
                        if (record.isDivider) {
                          return {
                            children: <div style={{ height: '1px', background: '#e8e8e8' }} />,
                            props: { colSpan: 3 },
                          };
                        }
                        return (
                          <Checkbox
                            disabled={true}
                            checked={record.dist.enabled || false}
                            onChange={e => {
                              updateDistribution(record.service, record.environment, {
                                enabled: e.target.checked,
                              });
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {text} (
                              {record.environment === 'production' ? 'Production' : 'Staging'})
                            </span>
                          </Checkbox>
                        );
                      },
                    },
                    {
                      title: 'Auto Publish',
                      key: 'auto_publish',
                      width: '150px',
                      render: (_: any, record: any) => {
                        if (record.isDivider) {
                          return { props: { colSpan: 0 } };
                        }
                        return (
                          <Checkbox
                            disabled={true}
                            checked={record.dist.auto_publish || false}
                            onChange={e =>
                              updateDistribution(record.service, record.environment, {
                                auto_publish: e.target.checked,
                              })
                            }
                          >
                            Auto Publish
                          </Checkbox>
                        );
                      },
                    },
                    {
                      title: 'Subtitles',
                      key: 'subtitles',
                      render: (_: any, record: any) => {
                        if (record.isDivider) {
                          return { props: { colSpan: 0 } };
                        }
                        return (
                          <Space wrap>
                            {SUBTITLE_LANGUAGES.map(lang => (
                              <Checkbox
                                key={lang.value}
                                checked={record.dist.subtitle_langs?.includes(lang.value) || false}
                                disabled={true}
                                onChange={e => {
                                  const newSubtitles = e.target.checked
                                    ? [...(record.dist.subtitle_langs || []), lang.value]
                                    : (record.dist.subtitle_langs || []).filter(
                                        (s: string) => s !== lang.value
                                      );
                                  updateDistribution(record.service, record.environment, {
                                    subtitle_langs: newSubtitles,
                                  });
                                }}
                              >
                                {lang.label}
                              </Checkbox>
                            ))}
                          </Space>
                        );
                      },
                    },
                  ]}
                  pagination={false}
                  bordered
                />
              </>
            )}
          </Card>

          {/* Save Button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'right',
              alignItems: 'center',
              gap: '16px',
              marginTop: '32px',
            }}
          >
            {/* <Card>
              <Form.Item label="Season Status" name="status" style={{ marginBottom: 0 }}>
                <Select
                  style={{ width: '200px' }}
                  value={
                    form.getFieldValue('status') === true ||
                    form.getFieldValue('status') === 'published'
                      ? 'published'
                      : 'unpublished'
                  }
                  onChange={value => {
                    form.setFieldValue('status', value);
                  }}
                >
                  <Select.Option value="published">Enabled</Select.Option>
                  <Select.Option value="unpublished">Disabled</Select.Option>
                </Select>
              </Form.Item>
            </Card> */}
            <Button type="primary" size="large" onClick={handleSave}>
              Save Season
            </Button>
          </div>
        </Form>
      </div>
    </Component.BasePageComponent>
  );
};

export default SeasonEdit2;
